/**
 * ResultScreen — renders the Roast_Result and drives the Staggered_Reveal.
 *
 * Owns the reveal timeline: the roast text and Cringe-O-Meter are shown
 * immediately on mount (initial reveal, AC 4.1); each recommendation plus
 * the closing line and CTA follow with per-item delays drawn from
 * {@link plan} in `[600, 1200]` ms (AC 4.2). The screen listens for any
 * pointer/keydown inside its shell; those events skip to the end
 * (AC 4.6) by flushing every hidden item to visible and advancing the
 * state machine via `skipReveal()` + `completeReveal()`.
 *
 * Reduced motion: under `prefers-reduced-motion: reduce` all scheduled
 * reveals collapse to `setTimeout(..., 0)` so the state transitions remain
 * observable (tests can still drive them) but without any actual delay.
 *
 * The reset button ("Roast Another Company") appears in the `result`
 * state and calls `controller.reset()`, returning the app to `idle`.
 *
 * See `.kiro/specs/marketing-roast-machine/design.md` — "Animations",
 * "Runtime Flow", and Requirements 4, 5.
 */

import type { AppController } from '../state/appController';
import type { AppState } from '../state/stateMachine';
import { plan, seededRng } from '../services/revealTimeline';
import { renderCringeOMeter } from './CringeOMeter';

type RevealState = Extract<AppState, { kind: 'revealing' | 'result' }>;

/**
 * Mount the result screen into `root`. Returns a teardown function that
 * clears pending timers, detaches listeners, and removes the DOM. Safe
 * to call twice.
 *
 * `initialState` is the controller state at mount time; it can be either
 * `revealing` (first entry after a successful submit) or `result` (e.g.
 * future retry paths that land directly on a fully-revealed state).
 */
export function mountResultScreen(
  root: HTMLElement,
  controller: AppController,
  initialState: RevealState,
): () => void {
  const { result } = initialState;

  // --- Shell + grid scaffolding ---
  const shell = document.createElement('div');
  shell.className = 'roast-shell result-screen';

  const grid = document.createElement('div');
  grid.className = 'roast-result-grid';
  shell.appendChild(grid);

  // Left column: roast text + Cringe-O-Meter (initial reveal).
  const leftCol = document.createElement('div');
  leftCol.className = 'result-left';

  const roastPara = document.createElement('p');
  roastPara.className = 'roast-text';
  roastPara.textContent = result.roastText;
  leftCol.appendChild(roastPara);

  leftCol.appendChild(renderCringeOMeter(result.cringeScore, result.cringeLabel));

  // Right column: recommendations, closing line, CTA (staggered).
  const rightCol = document.createElement('div');
  rightCol.className = 'result-right';

  const recsList = document.createElement('ol');
  recsList.className = 'recommendations';

  const hiddenNodes: HTMLElement[] = [];

  for (const rec of result.recommendations) {
    const li = document.createElement('li');
    li.className = 'reveal-hidden';
    li.textContent = rec;
    recsList.appendChild(li);
    hiddenNodes.push(li);
  }
  rightCol.appendChild(recsList);

  const closing = document.createElement('p');
  closing.className = 'closing-line reveal-hidden';
  closing.textContent = result.closingLine;
  rightCol.appendChild(closing);
  hiddenNodes.push(closing);

  const ctaWrap = document.createElement('div');
  ctaWrap.className = 'contact-cta reveal-hidden';
  ctaWrap.textContent = result.cta;
  rightCol.appendChild(ctaWrap);
  hiddenNodes.push(ctaWrap);

  grid.appendChild(leftCol);
  grid.appendChild(rightCol);

  // Reset button lives in a footer row; visible once we reach `result`.
  const footer = document.createElement('div');
  footer.className = 'result-footer';

  const resetButton = document.createElement('button');
  resetButton.type = 'button';
  resetButton.className = 'reset-button';
  resetButton.textContent = 'Roast Another Company';
  resetButton.style.display =
    initialState.kind === 'result' ? '' : 'none';
  footer.appendChild(resetButton);
  shell.appendChild(footer);

  root.appendChild(shell);

  // --- Reveal scheduling ---
  const timers: ReturnType<typeof setTimeout>[] = [];
  let torn = false;
  let skipped = false;

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Per AC 4.2: each rec revealed 600–1200 ms after the previous. For the
  // closing line and CTA, the spec leaves the exact cadence looser, so we
  // use a fixed 400 ms so total reveal stays well under the 60 s budget.
  const recDelays = prefersReducedMotion
    ? [0, 0, 0]
    : plan(3, seededRng(Date.now()));
  const TAIL_DELAY = prefersReducedMotion ? 0 : 400;

  /** Flip one item from `.reveal-hidden` to `.reveal-shown`. */
  const revealNode = (node: HTMLElement): void => {
    node.classList.remove('reveal-hidden');
    node.classList.add('reveal-shown');
  };

  /** Flush every remaining hidden item to visible and cancel timers. */
  const flushAll = (): void => {
    for (const t of timers) {
      clearTimeout(t);
    }
    timers.length = 0;
    for (const node of hiddenNodes) {
      if (node.classList.contains('reveal-hidden')) {
        revealNode(node);
      }
    }
  };

  /**
   * Schedule the cascade: rec 1 at t0+d0, rec 2 at t0+d0+d1, rec 3 at
   * t0+d0+d1+d2, closing at +TAIL, CTA at +TAIL+TAIL. Each step advances
   * the state machine so observers (tests, future instrumentation) see
   * the full progression.
   */
  const scheduleReveal = (): void => {
    if (initialState.kind !== 'revealing') {
      // Already at `result` — show everything immediately.
      flushAll();
      return;
    }

    const recNodes = hiddenNodes.slice(0, 3);
    let cumulative = 0;
    for (let i = 0; i < 3; i++) {
      cumulative += recDelays[i] ?? 0;
      const node = recNodes[i];
      if (node === undefined) {
        continue;
      }
      timers.push(
        setTimeout(() => {
          if (torn || skipped) {
            return;
          }
          revealNode(node);
          controller.advanceReveal();
        }, cumulative),
      );
    }

    cumulative += TAIL_DELAY;
    timers.push(
      setTimeout(() => {
        if (torn || skipped) {
          return;
        }
        revealNode(closing);
      }, cumulative),
    );

    cumulative += TAIL_DELAY;
    timers.push(
      setTimeout(() => {
        if (torn || skipped) {
          return;
        }
        revealNode(ctaWrap);
        controller.completeReveal();
      }, cumulative),
    );
  };

  /**
   * Skip handler: fired on any pointerdown/keydown inside the shell while
   * still revealing. Flushes the remaining hidden items and advances the
   * state machine.
   */
  const onSkip = (event: Event): void => {
    if (skipped || torn) {
      return;
    }
    if (controller.getState().kind !== 'revealing') {
      return;
    }
    // Clicks on the reset button should not be treated as skip events; the
    // reset button is hidden during revealing anyway, but be defensive.
    const target = event.target;
    if (target instanceof Node && resetButton.contains(target)) {
      return;
    }
    skipped = true;
    flushAll();
    controller.skipReveal();
    controller.completeReveal();
  };

  shell.addEventListener('pointerdown', onSkip);
  shell.addEventListener('keydown', onSkip);

  const onReset = (): void => {
    controller.reset();
  };
  resetButton.addEventListener('click', onReset);

  // Track state changes so we can reveal the reset button at the
  // revealing→result boundary. AppShell treats revealing+result as a
  // single mount lifetime, so we re-render minimally here rather than
  // tearing everything down.
  const unsubscribe = controller.subscribe((next) => {
    if (torn) {
      return;
    }
    if (next.kind === 'result') {
      resetButton.style.display = '';
    }
  });

  scheduleReveal();

  return () => {
    if (torn) {
      return;
    }
    torn = true;
    for (const t of timers) {
      clearTimeout(t);
    }
    timers.length = 0;
    shell.removeEventListener('pointerdown', onSkip);
    shell.removeEventListener('keydown', onSkip);
    resetButton.removeEventListener('click', onReset);
    unsubscribe();
    shell.remove();
  };
}
